import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useProfile } from '@/hooks/useProfile'
import apiClient from '@/lib/api-client'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { Loader2, Eye, EyeOff, ExternalLink, Globe, Lock } from 'lucide-react'
import { useDashboardEmbedded } from '@/hooks/useDashboardEmbedded'

export default function VisibilitySettings() {
  const { t } = useTranslation('dashboard')
  const embedded = useDashboardEmbedded()
  const { profile, isLoading, updateProfile } = useProfile()
  const [isUpdating, setIsUpdating] = useState(false)
  const [isSavingSeo, setIsSavingSeo] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [seoSuccess, setSeoSuccess] = useState(false)
  const [metaTitle, setMetaTitle] = useState('')
  const [metaDescription, setMetaDescription] = useState('')
  const [isIndexable, setIsIndexable] = useState(true)

  const isPublished = profile?.is_published ?? false

  useEffect(() => {
    if (profile) {
      setMetaTitle(profile.meta_title || '')
      setMetaDescription(profile.meta_description || '')
      setIsIndexable(profile.is_indexable ?? true)
    }
  }, [profile])

  const handleToggleVisibility = async () => {
    if (!profile) return

    setIsUpdating(true)
    try {
      await apiClient.put('/user/profile/visibility', {
        is_published: !isPublished,
      })

      await updateProfile()
      setShowSuccess(true)
      setTimeout(() => setShowSuccess(false), 2000)
    } catch (error) {
      console.error('Failed to update visibility:', error)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleSaveSeo = async () => {
    if (!profile) return

    setIsSavingSeo(true)
    try {
      await apiClient.put('/user/profile/visibility', {
        is_published: isPublished,
        is_indexable: isIndexable,
        meta_title: metaTitle || null,
        meta_description: metaDescription || null,
      })
      await updateProfile()
      setSeoSuccess(true)
      setTimeout(() => setSeoSuccess(false), 2000)
    } catch (error) {
      console.error('Failed to update SEO settings:', error)
    } finally {
      setIsSavingSeo(false)
    }
  }

  const handlePreview = () => {
    if (profile?.slug) {
      window.open(`/${profile.slug}`, '_blank')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="p-6 lg:p-8">
        <p className="text-gray-500">Profile not found</p>
      </div>
    )
  }

  return (
    <div className="p-6 lg:p-8 max-w-2xl">
      {!embedded && (
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{t('visibility.title')}</h1>
          <p className="text-gray-500">{t('visibility.subtitle')}</p>
        </div>
      )}

      {/* Current Status */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {isPublished ? (
              <>
                <Globe className="w-5 h-5 text-green-600" />
                <span>{t('visibility.status.published')}</span>
              </>
            ) : (
              <>
                <Lock className="w-5 h-5 text-amber-600" />
                <span>{t('visibility.status.private')}</span>
              </>
            )}
          </CardTitle>
          <CardDescription>
            {isPublished
              ? t('visibility.status.publishedDesc')
              : t('visibility.status.privateDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">{t('visibility.status.label')}</p>
              <p className={`text-lg font-semibold ${isPublished ? 'text-green-600' : 'text-amber-600'}`}>
                {isPublished ? t('visibility.status.published') : t('visibility.status.draft')}
              </p>
            </div>
            <Button
              variant={isPublished ? 'outline' : 'default'}
              onClick={handleToggleVisibility}
              disabled={isUpdating}
            >
              {isUpdating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('visibility.buttons.updating')}
                </>
              ) : showSuccess ? (
                t('visibility.buttons.updated')
              ) : isPublished ? (
                <>
                  <EyeOff className="w-4 h-4 mr-2" />
                  {t('visibility.buttons.unpublish')}
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-2" />
                  {t('visibility.buttons.publish')}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* SEO Settings */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{t('visibility.seo.title')}</CardTitle>
          <CardDescription>{t('visibility.seo.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isIndexable}
              onChange={(e) => setIsIndexable(e.target.checked)}
              className="rounded border-gray-300"
            />
            {t('visibility.seo.indexable')}
          </label>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('visibility.seo.metaTitle')}
            </label>
            <Input
              value={metaTitle}
              onChange={(e) => setMetaTitle(e.target.value)}
              placeholder={profile.business_name}
              maxLength={70}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('visibility.seo.metaDescription')}
            </label>
            <textarea
              value={metaDescription}
              onChange={(e) => setMetaDescription(e.target.value)}
              rows={3}
              maxLength={160}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              placeholder={t('visibility.seo.metaDescriptionPlaceholder')}
            />
          </div>
          <Button onClick={handleSaveSeo} disabled={isSavingSeo}>
            {isSavingSeo ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('visibility.seo.saving')}
              </>
            ) : seoSuccess ? (
              t('visibility.seo.saved')
            ) : (
              t('visibility.seo.save')
            )}
          </Button>
        </CardContent>
      </Card>

      {/* What publishing means */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{t('visibility.whatPublishing.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold">•</span>
              <span>{t('visibility.whatPublishing.accessibleAt', { slug: profile.slug })}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold">•</span>
              <span>{t('visibility.whatPublishing.anyoneWithLink')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold">•</span>
              <span>{t('visibility.whatPublishing.unpublished')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold">•</span>
              <span>{t('visibility.whatPublishing.unpublishAnyTime')}</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card>
        <CardHeader>
          <CardTitle>{t('visibility.preview.title')}</CardTitle>
          <CardDescription>
            {t('visibility.preview.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="outline"
            onClick={handlePreview}
            disabled={!profile.slug}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            {t('visibility.preview.openPreview')}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
